import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../hooks/useAuthCompat";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "../hooks/useApi";

const CartContext = createContext(null);
const GUEST_CART_KEY = "eb_guest_cart";

function readGuestCart() {
  try {
    return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const { isSignedIn } = useAuth();
  const api = useApi();
  const qc = useQueryClient();
  const [guestIds, setGuestIds] = useState(readGuestCart);
  const merged = useRef(false);

  const { data: serverCart } = useQuery({
    queryKey: ["cart"],
    queryFn: async () => (await api.get("/cart")).data,
    enabled: !!isSignedIn,
  });

  useEffect(() => {
    if (!isSignedIn) {
      merged.current = false;
      return;
    }
    if (merged.current) return;
    merged.current = true;
    const pending = readGuestCart();
    if (!pending.length) return;
    api.post("/cart/merge", { product_ids: pending }).then(() => {
      localStorage.removeItem(GUEST_CART_KEY);
      setGuestIds([]);
      qc.invalidateQueries({ queryKey: ["cart"] });
    }).catch(() => {});
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemIds = isSignedIn ? (serverCart?.items || []).map((i) => i.product_id) : guestIds;

  const addItem = useCallback(async (productId, title) => {
    if (isSignedIn) {
      try {
        await api.post("/cart/items", { product_id: productId });
        qc.invalidateQueries({ queryKey: ["cart"] });
        toast.success(`${title || "Item"} added to cart.`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not add to cart.");
      }
    } else {
      setGuestIds((prev) => {
        if (prev.includes(productId)) return prev;
        const next = [...prev, productId];
        localStorage.setItem(GUEST_CART_KEY, JSON.stringify(next));
        return next;
      });
      toast.success(`${title || "Item"} added to cart.`);
    }
  }, [isSignedIn, api, qc]);

  const removeItem = useCallback(async (productId) => {
    if (isSignedIn) {
      try {
        await api.delete(`/cart/items/${productId}`);
        qc.invalidateQueries({ queryKey: ["cart"] });
      } catch {
        toast.error("Could not remove item from cart.");
      }
    } else {
      setGuestIds((prev) => {
        const next = prev.filter((id) => id !== productId);
        localStorage.setItem(GUEST_CART_KEY, JSON.stringify(next));
        return next;
      });
    }
  }, [isSignedIn, api, qc]);

  const clearCart = useCallback(async () => {
    if (isSignedIn) {
      try {
        await api.delete("/cart");
        qc.invalidateQueries({ queryKey: ["cart"] });
      } catch {
        toast.error("Could not clear cart.");
      }
    } else {
      localStorage.removeItem(GUEST_CART_KEY);
      setGuestIds([]);
    }
  }, [isSignedIn, api, qc]);

  return (
    <CartContext.Provider value={{ itemIds, addItem, removeItem, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
